// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { bankDebtSchema, type BankDebt } from '@client/banks/types/debt';
import { zodResolver } from '@hookform/resolvers/zod';
import { useConst } from '@shared/ui-kit/hooks';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

const debtFormSchemas = {
  create: bankDebtSchema,
  update: bankDebtSchema,
} as const;

export type DebtFormVariant = keyof typeof debtFormSchemas;

export type DebtFormValues = BankDebt;

export type UseDebtFormParams =
  | {
      variant: 'create';
      defaultValues: BankDebt;
      disabled?: boolean;
    }
  | {
      variant: 'update';
      defaultValues: BankDebt;
      disabled?: boolean;
    };

export const useDebtForm = (params: UseDebtFormParams) => {
  const schema = useConst(() => debtFormSchemas[params.variant]);

  const form = useForm<DebtFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: params.defaultValues,
    disabled: params.disabled ?? false,
  });

  useEffect(() => {
    form.reset(params.defaultValues);
  }, [form, params.defaultValues]);

  return form;
};
